import React from "react";
import { ORG_TAB_ALIASES, ORG_TABS, type OrgTab } from "../sections/org/tabs";
import { COST_BREAKDOWNS, viewOf } from "../sections/insights/costViews";

export const SECTIONS = [
  "overview",
  "usage",
  "keys",
  "billing",
  "credits",
  "leaderboard",
  "organization",
  "account",
  "terms",
] as const;

export type Section = (typeof SECTIONS)[number];

// ``sub`` is a page's own view, e.g. #organization/visibility/efficiency.
export type Route = { section: Section; orgTab: OrgTab; sub?: string };

const HOME: Route = { section: "overview", orgTab: "home" };

export function routeHash(
  section: Section,
  orgTab: OrgTab = "home",
  sub?: string,
) {
  if (section === "organization")
    return `#organization/${orgTab}${sub ? `/${sub}` : ""}`;
  if (section === "overview") return sub ? `#usage/${sub}` : "#usage";
  return `#${section}${sub ? `/${sub}` : ""}`;
}

// null for a hash that is not ours, so the current route stays put.
export function parseRoute(hash: string): Route | null {
  const h = hash.replace(/^#/, "").split("?")[0];
  if (!h) return HOME;
  const [s, t, sub] = h.split("/");
  const found = SECTIONS.find((x) => x === s);
  if (!found) return null;
  // Personal pages carry their tab as the second segment: #usage/sessions.
  const section = found === "usage" ? "overview" : found;
  if (section !== "organization")
    return { section, orgTab: "home", ...(t ? { sub: t } : {}) };
  return {
    section,
    orgTab:
      ORG_TABS.find((x) => x.key === t)?.key ?? ORG_TAB_ALIASES[t] ?? "home",
    ...(sub ? { sub } : {}),
  };
}

/** An org link whose page name matches nothing, e.g. a typo or an old bookmark. */
export function isUnknownOrgTab(hash: string): boolean {
  const [s, t] = hash.replace(/^#/, "").split("?")[0].split("/");
  return (
    s === "organization" &&
    !!t &&
    !ORG_TABS.some((x) => x.key === t) &&
    !ORG_TAB_ALIASES[t]
  );
}

// Views that moved to another page; old links and bookmarks follow them.
const MOVED: [RegExp, string][] = [
  [
    /^#organization\/plans(?:\/[^?]*)?\?(?:.*&)?tab=api\b.*$/,
    "#organization/settings?tab=api",
  ],
  [
    /^#organization\/plans(?:\/[^?]*)?\?(?:.*&)?tab=credits\b.*$/,
    "#organization/settings?tab=billing",
  ],
  [
    /^#organization\/settings\?(?:.*&)?tab=teams\b.*$/,
    "#organization/plans?tab=teams",
  ],
  [
    /^#organization\/settings\?(?:.*&)?tab=members\b.*$/,
    "#organization/settings",
  ],
  [/^#organization\/coach\/mine\b.*$/, "#usage"],
];

/** Where an outdated hash now lives, or null when it is current. */
export function movedHash(hash: string): string | null {
  const [path, rawQuery] = hash.split("?");
  if (/^#organization\/(members|devices)(?:\/[^?]*)?$/.test(path)) {
    const query = new URLSearchParams(rawQuery);
    if (query.has("days") && !query.has("devicesDays")) {
      query.set("devicesDays", query.get("days")!);
      query.delete("days");
    }
    query.set("tab", "devices");
    return `#organization/plans?${query}`;
  }
  const match = path.match(
    /^#organization\/(visibility|analytics|costs)(?:\/([^/]+))?$/,
  );
  if (match) {
    const [, tab, sub] = match;
    const query = new URLSearchParams(rawQuery);
    const activity = ["sessions", "logs", "usecases"].includes(sub);
    if (COST_BREAKDOWNS[sub]) query.set("by", COST_BREAKDOWNS[sub]);
    if (sub === "flow") query.set("view", "flow");
    const next = activity
      ? routeHash("organization", "activity", sub)
      : routeHash("organization", "visibility", sub ? viewOf(sub) : undefined);
    const target = next + (query.size ? `?${query}` : "");
    if (tab !== "visibility" || target !== hash) return target;
  }
  for (const [from, to] of MOVED) if (from.test(hash)) return to;
  return null;
}

export function pushRoute(section: Section, orgTab?: OrgTab, sub?: string) {
  const { pathname, search, hash } = window.location;
  const [route, rawQuery] = hash.split("?");
  const previous = parseRoute(hash);
  const keepFilters =
    section === "organization" &&
    previous?.section === section &&
    previous.orgTab === orgTab &&
    (orgTab === "visibility" || orgTab === "activity");
  const params = new URLSearchParams(rawQuery ?? "");
  for (const key of [...params.keys()]) {
    if (key !== "r" && !(keepFilters && ["f", "by", "view"].includes(key)))
      params.delete(key);
  }
  const query = params.toString();
  const next =
    (routeHash(section, orgTab, sub) || (query ? "#" : "")) +
    (query ? `?${query}` : "");
  if (routeHash(section, orgTab, sub) !== route) {
    window.history.pushState(null, "", pathname + search + next);
    window.dispatchEvent(new Event(ROUTE_EVENT));
  }
}

/** Fired after an in-app navigation, which pushState does not announce. */
export const ROUTE_EVENT = "helm:route";
/** Dispatched on window by the account menu's "Take the tour". */
export const TOUR_EVENT = "helm:tour";

function follow() {
  const to = movedHash(window.location.hash);
  if (!to) return;
  const { pathname, search } = window.location;
  window.history.replaceState(null, "", pathname + search + to);
}

export function Redirect({ to }: { to: string }) {
  React.useEffect(() => {
    const { pathname, search } = window.location;
    window.history.replaceState(null, "", pathname + search + to);
    window.dispatchEvent(new Event(ROUTE_EVENT));
  }, [to]);
  return null;
}

export function useRoute(): [
  Route,
  (s: Section, t?: OrgTab, sub?: string) => void,
] {
  const [route, setRoute] = React.useState<Route>(() => {
    follow();
    return parseRoute(window.location.hash) ?? HOME;
  });
  React.useEffect(() => {
    const sync = () => {
      follow();
      const r = parseRoute(window.location.hash);
      if (r) setRoute(r);
    };
    const events = ["popstate", "hashchange", ROUTE_EVENT];
    events.forEach((e) => window.addEventListener(e, sync));
    // A Redirect mounted in the same commit fired before these listeners.
    sync();
    return () => events.forEach((e) => window.removeEventListener(e, sync));
  }, []);
  const navigate = React.useCallback(
    (section: Section, orgTab?: OrgTab, sub?: string) => {
      pushRoute(section, orgTab, sub);
      setRoute({ section, orgTab: orgTab ?? "home", ...(sub ? { sub } : {}) });
    },
    [],
  );
  return [route, navigate];
}
